import * as React from 'react';
import { compose, withHandlers, withState, withPropsOnChange } from 'recompose';
import { Link } from 'react-router-dom';
import { find, includes, get, isObject } from 'lodash';
import * as classnames from 'classnames';
import { format, getDate, getDay, getMonth, isToday } from 'date-fns';
import { czechHolidays, dayNames } from './_employeeCalendarEnums';
import CalendarIcon from 'material-ui/svg-icons/action/today';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const csLocale = require('date-fns/locale/cs');

const grid = 0;

const getItemStyle = (isDragging, draggableStyle) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: 'none',
  padding: grid * 2,
  margin: `0 0 ${grid}px 0`,

  // change background colour if dragging
  background: isDragging ? 'lightgreen' : 'transparent',

  // styles we need to apply on draggables
  ...draggableStyle
});

const getListStyle = isDraggingOver => ({
  background: isDraggingOver ? 'lightblue' : 'transparent',
  padding: grid
});

const reorder = (list: any, startIndex: any, endIndex: any) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const enhance = compose<any, any>(
  withState('reorderedEmployees', 'reorderEmployees', []),
  withHandlers({
    onDragEnd: ({ employees, reorderEmployees, reorderedEmployees }) => result => {
      if (!result.destination) {
        return;
      }

      const employeesToReorder = reorderedEmployees.length ? reorderedEmployees : employees;

      const items = reorder(employeesToReorder, result.source.index, result.destination.index);

      reorderEmployees(items);
    }
  }),
  withPropsOnChange(['employees', 'reorderedEmployees'], ({ reorderedEmployees, employees }) => ({
    employeesToShow: reorderedEmployees.length ? reorderedEmployees : employees
  }))
);

const calendarClasses = (day, index) => {
  const currentMonth = getMonth(day);
  const currentDate = getDate(day);
  const currentDay = getDay(day);
  const dayIsToday = isToday(day);
  const isHoliday = find(czechHolidays, { day: currentDate, month: currentMonth }) ? true : false;
  const startOfMonth = index === 0 || currentDate === 1;

  return classnames(
    'hr-calendar-day',
    { 'with-month-label': startOfMonth },
    { 'with-month-divider': currentDate === 1 },
    { 'start-of-week': currentDay === 1 },
    { weekend: includes([6, 0], currentDay) },
    { holiday: isHoliday },
    { today: dayIsToday }
  );
};

const RenderEmployee = ({
  employee,
  days,
  dayPlans,
  setEditedEntities,
  toggleDialog,
  entity,
  canChangeDayPlans,
  showEndOfWeekCalendars,
  linkToEmployeeActive
}) => {
  const employeeDayPlans = get(dayPlans, employee.id, []);
  return (
    <div key={employee.id} className="hr-calendar-employee">
      <div className="hr-calendar-employee-name text-hidden">{get(employee, 'fullName', '')}</div>
      {linkToEmployeeActive && (
        <Link
          to={{ pathname: `/hr/employee/detail/${employee.id}`, state: { shiftCycleId: get(entity, 'id') } }}
          style={{ textDecoration: 'none', color: 'inherit' }}
          className="hr-calendar-employee-name-holder"
        >
          <div className="hr-calendar-employee-name">{get(employee, 'fullName', '')}</div>
        </Link>
      )}
      {!linkToEmployeeActive && (
        <div className="hr-calendar-employee-name-holder">
          <div className="hr-calendar-employee-name">{get(employee, 'fullName', '')}</div>
        </div>
      )}
      {days.map((day, index) => {
        const endOfWeek = getDay(day) === 0;
        const dayPlan = find(employeeDayPlans, { visualizedDate: format(day, 'YYYY-MM-DD') });
        return (
          <React.Fragment key={`${employee.id}-${day}`}>
            <div key={index} className={calendarClasses(day, index)} title={get(dayPlan, 'description')}>
              <div
                className="hr-calendar-day-corner"
                style={{
                  borderColor: `${get(dayPlan, 'color', '#eee')} ${get(
                    dayPlan,
                    'color',
                    '#eee'
                  )} transparent transparent`
                }}
              />
              <div
                className={classnames('hr-calendar-day--dialog-opened', {
                  'hr-calendar-day--clickable': canChangeDayPlans
                })}
                onClick={() => {
                  toggleDialog(true);
                  setEditedEntities({ dayPlan, employee, isCreating: !isObject(dayPlan), day });
                }}
              >
                {dayPlan && get(dayPlan, 'label', '')}
                {!dayPlan && <span>&nbsp;</span>}
              </div>
            </div>
            {showEndOfWeekCalendars && endOfWeek && (
              <div className="hr-calendar-day">
                <Link
                  to={{
                    pathname: `/hr/employee/detail/${employee.id}`,
                    state: { shiftCycleId: get(entity, 'id'), defaultDate: day }
                  }}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <CalendarIcon className="hr-calendar-icon" />
                </Link>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const View: React.SFC<any> = ({
  days,
  dayPlans,
  toggleDialog = () => false,
  setEditedEntities = () => false,
  entity = {},
  canChangeDayPlans,
  showEndOfWeekCalendars,
  linkToEmployeeActive,
  onDragEnd,
  employeesToShow
}) => (
  <>
    <div className="hr-calendar-container">
      <div className="hr-calendar-header">
        {days.map((day, index) => {
          const currentDate = getDate(day);
          const currentDay = getDay(day);
          const endOfWeek = getDay(day) === 0;
          const startOfMonth = index === 0 || currentDate === 1;

          return (
            <React.Fragment key={`header-${day}`}>
              <div
                data-month={startOfMonth ? format(day, 'MMMM', { locale: csLocale }) : null}
                className={calendarClasses(day, index)}
              >
                <div>{dayNames[currentDay]}</div>
                {format(day, 'DD')}
              </div>
              {showEndOfWeekCalendars && endOfWeek && <div className="hr-calendar-day" />}
            </React.Fragment>
          );
        })}
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided, snapshot) => (
            <div ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)}>
              {employeesToShow.map((employee, index) => (
                <Draggable key={employee.id} draggableId={employee.id} index={index}>
                  {(providedChild: any, snapshotChild: any) => (
                    <div
                      ref={providedChild.innerRef}
                      {...providedChild.draggableProps}
                      {...providedChild.dragHandleProps}
                      style={getItemStyle(snapshotChild.isDragging, providedChild.draggableProps.style)}
                    >
                      <RenderEmployee
                        key={`${employee.id}${index}`}
                        employee={employee}
                        days={days}
                        dayPlans={find(dayPlans, employee.id)}
                        toggleDialog={toggleDialog}
                        setEditedEntities={setEditedEntities}
                        entity={entity}
                        canChangeDayPlans={canChangeDayPlans}
                        showEndOfWeekCalendars={showEndOfWeekCalendars}
                        linkToEmployeeActive={linkToEmployeeActive}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
    <div>
      <h3>Legenda</h3>
      <div style={{ display: 'flex' }}>
        <div className="hr-calendar-day legend today">&nbsp;</div> dnešek
      </div>
      <div style={{ display: 'flex' }}>
        <div className="hr-calendar-day legend weekend">&nbsp;</div> víkend
      </div>
      <div style={{ display: 'flex' }}>
        <div className="hr-calendar-day legend holiday">&nbsp;</div> státní svátek
      </div>
    </div>
  </>
);

export default enhance(View);



// WEBPACK FOOTER //
// ./src/components/employeeCalendar/EmployeeCalendar.tsx